/**
 * THE ONE CSV MACHINERY — v19 D4 (proposal §5.4), extracted from `CrfsListPage.tsx`.
 *
 * ===========================================================================
 * WHAT AN EXPORT IS, ON THIS PRODUCT: THE VISIBLE STRINGS, NOTHING ELSE
 * ===========================================================================
 * An export is a CHANNEL (rule 14), and it is exactly the channel masking gets forgotten
 * on. Every export in this app therefore serialises THE STRINGS ALREADY ON SCREEN — the
 * same cell values the reader can see — never the raw row. `formatMoney` has already
 * turned a refused figure into `'•••'` before it reaches a cell, so a seat without
 * `amounts.view` cannot produce a file with a digit it was refused; it produces a file
 * with the marker, which is the honest record of what that seat was shown.
 *
 * `csvField` is BYTE-IDENTICAL to the helper `CrfsListPage.tsx` carries (`csv.test.ts`
 * reads that file as text and holds the two bodies equal) — the list page is re-pointed
 * at this module in a SERIAL commit, not here (a PARALLEL unit writes only its own
 * subtree). Until then this is the single copy the dashboards/reports exports use and
 * the mirror test is what stops the pair drifting.
 *
 * `downloadCsv` is the Blob + anchor hand-off the list page already ships; jsdom has
 * neither object URLs nor anchor navigation, so tests stub `URL.createObjectURL` and
 * spy `HTMLAnchorElement.prototype.click` (`crfs-list.test.tsx`'s own idiom).
 */

/** Quote a field when it carries a comma, quote, CR or LF — RFC 4180's rule. */
export function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Header row + one line per row, CRLF-joined. Every cell is a STRING the caller rendered. */
export function rowsToCsv(headers: readonly string[], rows: readonly (readonly string[])[]): string {
  const lines = [headers.map(csvField).join(',')];
  for (const row of rows) lines.push(row.map(csvField).join(','));
  return lines.join('\r\n');
}

/** Hand the file to the browser. Returns the text so a caller can assert on it. */
export function downloadCsv(filename: string, csv: string): string {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
  return csv;
}
